/**
 * gatewayErrorHandler.js
 *
 * Interpreta erros estruturados retornados pelo backend quando o gateway
 * de pagamento rejeita dados do usuário.
 */

/**
 * Extrai a lista de FieldErrors de uma resposta de erro do axios.
 * @param {Error} error
 * @returns {{ field: string, step: number, message: string }[]}
 */
export function extractGatewayErrors(error) {
  return error?.response?.data?.gateway_errors || [];
}

/**
 * Verifica se o erro contém gateway_errors estruturados.
 * @param {Error} error
 * @returns {boolean}
 */
export function hasGatewayErrors(error) {
  const errors = extractGatewayErrors(error);
  return errors.length > 0;
}

/**
 * Agrupa os FieldErrors por step.
 * @param {{ field: string, step: number, message: string }[]} fieldErrors
 * @returns {Map<number, { field: string, step: number, message: string }[]>}
 */
export function groupErrorsByStep(fieldErrors) {
  const map = new Map();
  for (const fe of fieldErrors) {
    const step = fe.step || 1;
    if (!map.has(step)) map.set(step, []);
    map.get(step).push(fe);
  }
  return map;
}

/**
 * Retorna o step mais baixo que contém erros.
 * @param {{ field: string, step: number, message: string }[]} fieldErrors
 * @returns {number}
 */
export function firstErrorStep(fieldErrors) {
  if (!fieldErrors.length) return 1;
  return Math.min(...fieldErrors.map(fe => fe.step || 1));
}

/**
 * Constrói um objeto de erros indexados por field.
 * @param {{ field: string, step: number, message: string }[]} fieldErrors
 * @returns {Record<string, string>}
 */
export function fieldErrorsToMap(fieldErrors) {
  const map = {};
  for (const fe of fieldErrors) {
    if (fe.field) {
      map[fe.field] = fe.message;
    }
  }
  return map;
}

function normalizeField(field) {
  if (!field) return '';

  const value = String(field)
    .toLowerCase()
    .replace(/\./g, '_')
    .replace(/\s+/g, '_');

  if ([
    'card_number',
    'credit_card_number',
    'creditcard_number',
    'cardnumber',
  ].includes(value)) {
    return 'card_number';
  }

  if (['card_name', 'holder_name', 'credit_card_holder_name', 'card_holder_name'].includes(value)) {
    return 'card_name';
  }

  if (['card_cvv', 'cvv', 'credit_card_cvv'].includes(value)) {
    return 'card_cvv';
  }

  if (['card_expiry', 'card_expiration', 'expiry', 'expiration_date'].includes(value)) {
    return 'card_expiry_month';
  }

  if (['cep', 'postalcode', 'postal_code', 'customer_postal_code'].includes(value)) {
    return 'postal_code';
  }

  if ([
    'numero',
    'number',
    'addressnumber',
    'address_number',
    'customer_address_number',
  ].includes(value)) {
    return 'address_number';
  }

  return value;
}

function pickMessage(item) {
  return item?.message || item?.description || item?.error || '';
}

function isCepRelatedText(text) {
  if (!text) return false;
  const normalized = String(text)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

  return normalized.includes('cep');
}

function inferStepFromFieldOrText(field, text, fallbackStep = 2) {
  if (field === 'postal_code' || field === 'address_number' || isCepRelatedText(text)) {
    return 1;
  }
  return fallbackStep;
}

/**
 * Normaliza erros de validação vindos do backend para o formato FieldError.
 * Suporta gateway_errors, errors e formatos diretos (code/message/step).
 *
 * @param {Error} error
 * @param {{ defaultStep?: number }} options
 * @returns {{ field: string, step: number, message: string, code?: string }[]}
 */
export function normalizeValidationErrors(error, options = {}) {
  const defaultStep = options.defaultStep || 2;
  const data = error?.response?.data || {};

  const structuredGateway = Array.isArray(data.gateway_errors) ? data.gateway_errors : [];
  if (structuredGateway.length > 0) {
    return structuredGateway.map((item) => {
      const field = normalizeField(item?.field || '');
      const message = pickMessage(item) || 'Verifique os dados informados.';
      return {
        field,
        step: Number(item?.step) || inferStepFromFieldOrText(field, message, defaultStep),
        message,
        code: item?.code || data?.code || undefined,
        action: item?.action || data?.action || undefined,
      };
    });
  }

  const genericList = Array.isArray(data.errors) ? data.errors : [];
  if (genericList.length > 0) {
    return genericList.map((item) => {
      const field = normalizeField(item?.field || data?.field || '');
      const message = pickMessage(item) || pickMessage(data) || 'Verifique os dados informados.';
      return {
        field,
        step: Number(item?.step || data?.step) || inferStepFromFieldOrText(field, message, defaultStep),
        message,
        code: item?.code || data?.code || undefined,
        action: item?.action || data?.action || undefined,
      };
    });
  }

  const directMessage = pickMessage(data);
  const directCode = data?.code;
  const directField = normalizeField(data?.field || '');

  if (directMessage || directCode) {
    const isCepError = directCode === 'invalid_creditCard' && isCepRelatedText(directMessage);
    const field = directField || (isCepError ? 'postal_code' : '');
    return [{
      field,
      step: Number(data?.step) || inferStepFromFieldOrText(field, directMessage, defaultStep),
      message: directMessage || 'Não foi possível validar os dados do pagamento.',
      code: directCode,
      action: data?.action || undefined,
    }];
  }

  return [];
}
